/** tm/components/GovSubPicker.tsx — 공고 「업종 › 세부업종」 고르기 (2026-10-05g 지도사 확정 「추천대로」 · 2026-10-05q 「분야라는 개념이 익숙치 않다」)
 *
 *  등록 양식 · 공고 검토함 패널(같은 GovProgramForm)에서 쓴다.
 *   · 업종 칩(복수) — 무관 · 도소매업 · 제조업 · … · 농림축수산업 · 기타
 *   · 고른 업종마다 「▾ 제조업 세부업종」 상자 — 안 고르면 그 업종 전체 · 고르면 그 세부업종 업체만
 *   · 「무관」 = 업종 상관없이 — 「▾ 무관 세부업종」(세부업종 이름 전체 · 같은 이름은 한 번 · GOV_FIELD_NAMES)에서 고르면 어느 업종이든 그 세부업종 업체 모두
 *     (무관 › 수산 = 수산물 가공 제조 · 수산 유통 도소매 · 양식업)
 *   · 업종을 골랐을 때 이미 켜진 무관 › 세부업종은 작은 「▾ 무관 세부업종」 상자에 「무관 › 해양 ✕」 칩으로(눌러서 뺀다) · 더하려면 접힌 「▸ 무관 세부업종 더하기」
 *  켜진 칩은 전부 «하나라도» (엔진 bizGate · 2026-10-05q) — 「제조업 › 조선·선박」 + 「무관 › 해양」 = 조선·선박 제조업체 또는 업종 상관없이 해양 업체
 *  저장: 업종 = target_biz_types · 세부업종 = conditions.subs(키 'P›S' · 무관 › S = '*›S')
 *  자동으로 켠 키(사업명 · 본문 · AI — conditions.sec_auto · 사업명 미리보기)는 점선 + 「자동」 표시 · 끄면 sec_off 로 남겨 다시 켜지지 않게 한다.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { C } from './ui';
import type { GovConditions } from '../types';
import { GOV_PARENTS, GOV_SUBS, GOV_SUB_INFO, GOV_FIELD_NAMES, ANY_PARENT, subKey, parseSubKey, isSubKey, fieldTip, expandSubKeys } from './GovTaxonomy';

const TONE = { bd: '#AFA9EC', bg: '#EEEDFE', fg: '#3C3489' };
const BOX = { bd: 'rgba(175,169,236,0.45)', hint: '#9CA3AF', sub: '#6B7280' };
const listOf = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const uniq = (a: string[]) => a.filter((x, i) => a.indexOf(x) === i);
const parentShortOf = (p: string) => (p === 'IT/SW개발업' ? 'IT/SW' : p);

/** 칩 — 켬(굵게) · 자동(점선 + 「자동」) · 자동인데 뺌(점선 · 흐리게) */
function SubPill({ on, auto, off, onClick, children, title }: { on: boolean; auto?: boolean; off?: boolean; onClick: () => void; children: ReactNode; title?: string }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-pressed={on}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, fontSize: 11, lineHeight: 1.35, cursor: 'pointer', fontFamily: 'inherit',
        border: `1px ${(auto && on) || off ? 'dashed' : 'solid'} ${on ? TONE.bd : off ? C.borderDk : C.border}`,
        background: on ? TONE.bg : C.white, color: on ? TONE.fg : off ? C.muted : C.sub, fontWeight: on ? 600 : 400,
      }}>
      {children}
      {auto && on && <span style={{ fontSize: 10, fontWeight: 700, padding: '0 4px', borderRadius: 3, border: '1px solid currentColor', lineHeight: '14px', opacity: 0.85 }}>자동</span>}
    </button>
  );
}

/** 상자 머리 — 「▾ 제조업 세부업종 (설명)」 · 접을 수 있으면 단추 */
function BoxHead({ open, title, hint, onToggle }: { open: boolean; title: string; hint?: ReactNode; onToggle?: () => void }) {
  const st = { display: 'block', width: '100%', padding: 0, marginBottom: open ? 5 : 0, border: 'none', background: 'none', fontFamily: 'inherit', textAlign: 'left' as const, fontSize: 11, fontWeight: 500, color: TONE.fg, lineHeight: 1.5 };
  const body = <>{open ? '▾' : '▸'} {title}{hint ? <span style={{ fontWeight: 400, color: BOX.hint }}> {hint}</span> : null}</>;
  return onToggle
    ? <button type="button" onClick={onToggle} aria-expanded={open} style={{ ...st, cursor: 'pointer' }}>{body}</button>
    : <div style={st}>{body}</div>;
}
const boxStyle = { marginTop: 6, padding: '8px 10px', borderRadius: 8, background: C.white, border: `1px solid ${BOX.bd}` };

interface Props {
  biz: string[];
  onBiz: (next: string[]) => void;
  conditions: GovConditions | null;
  onConditions: (next: GovConditions) => void;
  /** 저장 전 사업명으로 알아본 키 (POST /gov-programs/preview targets) */
  nameAuto?: string[];
  /** 업종이 비었을 때 덧붙일 안내 (R&D 기본 업종 등) */
  emptyHint?: ReactNode;
}

/** 업종 · 세부업종 상태 계산 — 양식 밖(검토함 표 인라인 편집 등)에서도 같은 규칙을 쓰도록 분리 */
export function subState(conditions: GovConditions | null, nameAuto: string[] = []) {
  const c: GovConditions = conditions || {};
  // 저장된 옛 키(「바이오·제약·의료기기」 · 「항공·드론·방산」 …)는 새 키 둘로 읽는다(엔진 OLD_KEYS 와 같다 — 2026-10-05m)
  const subs = expandSubKeys(listOf(c.subs)), off = expandSubKeys(listOf(c.sec_off));
  const autoItems = c.sec_auto && Array.isArray(c.sec_auto.items) ? c.sec_auto.items : [];
  const autoKs = uniq([...expandSubKeys(autoItems.map(x => x.k)), ...expandSubKeys(nameAuto)]).filter(isSubKey);
  const isOn = (k: string) => !off.includes(k) && (subs.includes(k) || nameAuto.includes(k));
  return { c, subs, off, autoKs, isOn };
}

/** 켜진 칩 → 한 줄 글 「제조업 › 조선·선박 · 무관 › 해양」 (업종만 고른 것은 「제조업」 · 엔진 bizText 와 같은 꼴) */
export function pickedText(biz: string[], keys: string[]): string {
  const by: Record<string, string[]> = {};
  const order: string[] = [];
  for (const b of biz) if (!order.includes(b)) { order.push(b); by[b] = []; }
  for (const k of keys) {
    const x = parseSubKey(k); if (!x) continue;
    if (!by[x.p]) { by[x.p] = []; if (x.p !== ANY_PARENT) order.push(x.p); }
    if (!by[x.p].includes(x.s)) by[x.p].push(x.s);
  }
  if (by[ANY_PARENT]) order.push(ANY_PARENT);
  return order.map(p => { const n = p === ANY_PARENT ? '무관' : p; return by[p].length ? `${n} › ${by[p].join('·')}` : n; }).join(' · ');
}

export default function GovSubPicker({ biz, onBiz, conditions, onConditions, nameAuto = [], emptyHint }: Props) {
  const { c, subs, off, autoKs, isOn } = subState(conditions, nameAuto);
  const [anyOpen, setAnyOpen] = useState(false);   // 무관 세부업종 목록 — 고른 · 자동 키가 없으면 접어 둔다(대부분의 공고는 세부업종 없이 무관)
  const write = (nSubs: string[], nOff: string[]) => {
    const next: GovConditions = { ...c };
    const s = uniq(nSubs), o = uniq(nOff);
    if (s.length) next.subs = s; else delete next.subs;
    if (o.length) next.sec_off = o; else delete next.sec_off;
    onConditions(next);
  };
  /** 키 끄기 — 자동으로 켠 키는 sec_off 로 남긴다 */
  const offKeys = (keys: string[], nSubs: string[], nOff: string[]) => {
    for (const k of keys) { nSubs = nSubs.filter(x => x !== k); if (autoKs.includes(k) && !nOff.includes(k)) nOff = [...nOff, k]; }
    return { nSubs, nOff };
  };
  const toggleKey = (k: string) => {
    const x = parseSubKey(k); if (!x) return;
    if (x.p === ANY_PARENT) setAnyOpen(true);   // 무관 세부업종을 만진 뒤에는 마지막 것을 빼도 상자를 접지 않는다(눌러 둔 자리가 사라지지 않게)
    if (isOn(k)) { const r = offKeys([k], subs, off); write(r.nSubs, r.nOff); return; }
    write([...subs, k], off.filter(y => y !== k));
    if (x.p !== ANY_PARENT && !biz.includes(x.p)) onBiz([...biz, x.p]);   // 세부업종을 고르면 그 업종도 켠다
  };
  const keysOf = (p: string) => uniq([...subs, ...autoKs]).filter(k => { const x = parseSubKey(k); return !!x && x.p === p && isOn(k); });
  const toggleParent = (p: string) => {
    if (biz.includes(p)) {
      onBiz(biz.filter(x => x !== p));
      const r = offKeys(keysOf(p), subs, off);   // 업종을 끄면 그 세부업종도 함께 뺀다
      if (r.nSubs.length !== subs.length || r.nOff.length !== off.length) write(r.nSubs, r.nOff);
    } else onBiz([...biz, p]);
  };
  const setNone = () => {
    onBiz([]);
    const r = offKeys(uniq([...subs, ...autoKs]).filter(k => { const x = parseSubKey(k); return !!x && x.p !== ANY_PARENT && isOn(k); }), subs, off);
    if (r.nSubs.length !== subs.length || r.nOff.length !== off.length) write(r.nSubs, r.nOff);
    setAnyOpen(true);   // 「무관」을 누르면 무관 세부업종 목록을 연다
  };
  // 세부업종 키가 켜져 있는데 업종 칩이 빠진 경우(자동 체크 · 옛 값) — 그 업종 상자도 보여 준다
  const implied = GOV_PARENTS.filter(p => !biz.includes(p) && keysOf(p).length > 0);
  const shown = GOV_PARENTS.filter(p => (biz.includes(p) || implied.includes(p)) && GOV_SUBS[p].length > 0);
  const chipTitle = (k: string, name: string) => {
    const tip = parseSubKey(k)?.p === ANY_PARENT ? fieldTip(name) : GOV_SUB_INFO[name];
    const on = isOn(k), auto = autoKs.includes(k);
    return [tip, auto && on ? '✓ 사업명·본문에서 자동으로 체크 — 아니면 눌러서 빼세요' : '', auto && !on && off.includes(k) ? '자동으로 찾았지만 «아님»으로 뺀 세부업종 — 누르면 다시 켭니다' : ''].filter(Boolean).join('\n\n') || undefined;
  };
  const noneOn = biz.length === 0 && implied.length === 0;
  const anyOn = uniq([...subs, ...autoKs]).filter(k => parseSubKey(k)?.p === ANY_PARENT && isOn(k));
  const bizOn = GOV_PARENTS.filter(p => biz.includes(p) || implied.includes(p)).concat(biz.filter(b => !GOV_PARENTS.includes(b)));
  const onKeys = uniq([...subs, ...autoKs]).filter(isOn);
  const summary = pickedText(bizOn, onKeys);
  const listOpen = anyOpen || anyOn.length > 0;   // 무관이면 고른 키가 있을 때 늘 펼침
  // 무관 세부업종 전체 목록 (한 줄 — 업종별 묶음 제목을 두지 않는다 · 2026-10-05p) · 목록에 없는 옛 키가 켜져 있으면 앞에 둬서 뺄 수 있게
  const anyList = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      {anyOn.filter(k => !GOV_FIELD_NAMES.includes(parseSubKey(k)?.s || '')).map(k => {
        const s = parseSubKey(k)?.s || k;
        return <SubPill key={k} on auto={autoKs.includes(k)} onClick={() => toggleKey(k)} title={[chipTitle(k, s), '누르면 뺍니다'].filter(Boolean).join('\n\n')}>{s} ✕</SubPill>;
      })}
      {GOV_FIELD_NAMES.map(s => {
        const k = subKey(ANY_PARENT, s);
        return <SubPill key={k} on={isOn(k)} auto={autoKs.includes(k)} off={autoKs.includes(k) && off.includes(k)} onClick={() => toggleKey(k)} title={chipTitle(k, s)}>{s}</SubPill>;
      })}
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <SubPill on={noneOn} onClick={setNone} title="업종 상관없이 — 아래 「무관 세부업종」에서 고르면 어느 업종이든 그 세부업종 업체만">무관</SubPill>
        {GOV_PARENTS.map(p => (
          <SubPill key={p} on={biz.includes(p) || implied.includes(p)} auto={!biz.includes(p) && implied.includes(p)} onClick={() => (implied.includes(p) && !biz.includes(p) ? onBiz([...biz, p]) : toggleParent(p))}
            title={implied.includes(p) && !biz.includes(p) ? `세부업종이 자동으로 켜져 ${p}도 연결됩니다 — 누르면 업종도 고정합니다` : undefined}>{p}</SubPill>
        ))}
      </div>
      {shown.map(p => {
        const n = keysOf(p).length;
        return (
          <div key={p} style={boxStyle}>
            <BoxHead open title={`${p} 세부업종`} hint={`(${n ? `${n}개 고름 · 그 세부업종 업체만` : `안 고르면 ${parentShortOf(p)} 전체`} · 업체는 업종·주요 제품으로 자동 판정)`} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {GOV_SUBS[p].map(s => {
                const k = subKey(p, s);
                return <SubPill key={k} on={isOn(k)} auto={autoKs.includes(k)} off={autoKs.includes(k) && off.includes(k)} onClick={() => toggleKey(k)} title={chipTitle(k, s)}>{s}</SubPill>;
              })}
            </div>
          </div>
        );
      })}
      {/* 무관 세부업종 (2026-10-05q) — 업종 「무관」이면 목록 상자 하나 · 업종을 골랐으면 켜진 무관 키만 작은 상자(✕ 로 뺌) + 접힌 「더하기」 */}
      {noneOn ? (
        <div style={boxStyle}>
          <BoxHead open={listOpen} title={listOpen ? '무관 세부업종' : '무관 세부업종 고르기'}
            hint={listOpen ? `— ${anyOn.length ? `${anyOn.length}개 고름 · ` : ''}업종 상관없이 그 세부업종 업체 모두 (예: 수산 = 수산물 가공 · 유통 · 양식)` : '(특정 세부업종 업체만일 때 — 예: 수산 · 식품 · 반도체)'}
            onToggle={anyOn.length ? undefined : () => setAnyOpen(!listOpen)} />
          {listOpen && anyList}
        </div>
      ) : (
        <div style={boxStyle}>
          {anyOn.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <BoxHead open title="무관 세부업종" hint="— 업종 상관없이 그 세부업종 업체도 (위 업종과 하나라도)" />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {anyOn.map(k => {
                  const s = parseSubKey(k)?.s || k;
                  return <SubPill key={k} on auto={autoKs.includes(k)} onClick={() => toggleKey(k)} title={[chipTitle(k, s), '누르면 뺍니다'].filter(Boolean).join('\n\n')}>무관 › {s} <span aria-hidden style={{ opacity: 0.7 }}>✕</span></SubPill>;
                })}
              </div>
            </div>
          )}
          <BoxHead open={anyOpen} title="무관 세부업종 더하기" hint="(업종 상관없이 그 세부업종 업체도 추천 — 예: 무관 › 해양)" onToggle={() => setAnyOpen(!anyOpen)} />
          {anyOpen && anyList}
        </div>
      )}
      {summary && onKeys.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: BOX.sub, lineHeight: 1.5 }}>
          하나라도 맞으면 추천 — <b style={{ color: TONE.fg, fontWeight: 600 }}>{summary}</b>
        </div>
      )}
      {emptyHint}
    </div>
  );
}
